import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import { SUBJECTS } from '../../../constants';
import { ImageUpload } from '../shared';

const QuizEditModal = ({ 
  isOpen, 
  onClose, 
  mode, 
  quiz = {},
  contentSubject,
  handleSubmitQuiz,
  formErrors = {},
  currentUser = null
}) => {
  const [quizData, setQuizData] = useState({
    type: quiz?.type || 'multiple-choice',
    subject: quiz?.subject || '',
    question: quiz?.question || '',
    choices: quiz?.choices || ['', '', '', ''],
    answer: quiz?.answer || '',
    explanation: quiz?.explanation || '',
    englishSentence: quiz?.englishSentence || '',
    japaneseTranslation: quiz?.japaneseTranslation || '',
    leftItems: quiz?.leftItems || [],
    rightItems: quiz?.rightItems || [],
    correctMatches: quiz?.correctMatches || [],
    timestamp: quiz?.timestamp || Date.now(),
    // 画像関連の新規フィールド（後方互換性を保つ）
    questionImage: quiz?.questionImage || null,
    hasImage: quiz?.hasImage || false
  });

  // propsの変更を監視
  useEffect(() => {
    setQuizData({
      type: quiz?.type || 'multiple-choice',
      subject: quiz?.subject || '',
      question: quiz?.question || '',
      choices: quiz?.choices || ['', '', '', ''],
      answer: quiz?.answer || '',
      explanation: quiz?.explanation || '',
      englishSentence: quiz?.englishSentence || '',
      japaneseTranslation: quiz?.japaneseTranslation || '',
      leftItems: quiz?.leftItems || [],
      rightItems: quiz?.rightItems || [],
      correctMatches: quiz?.correctMatches || [],
      timestamp: quiz?.timestamp || Date.now(),
      // 画像関連の新規フィールド（後方互換性を保つ）
      questionImage: quiz?.questionImage || null,
      hasImage: quiz?.hasImage || false
    });
  }, [quiz]);
  
  if (!isOpen) return null;

  const handleSubmit = () => {
    handleSubmitQuiz(quizData);
  };

  const updateQuizData = (updates) => {
    setQuizData(prev => ({ ...prev, ...updates }));
  };

  // 画像アップロード時のハンドラー
  const handleImageUploaded = (imageData) => {
    updateQuizData({
      questionImage: imageData,
      hasImage: true
    });
  };

  // 画像削除時のハンドラー
  const handleImageRemoved = () => {
    updateQuizData({
      questionImage: null,
      hasImage: false
    });
  };

  // 多選択肢問題のフォーム
  const renderMultipleChoiceForm = () => (
    <>
      {/* 問題画像のアップロード */}
      <div>
        <label className="block text-sm font-medium text-purple-700 mb-2">
          問題画像（任意）
        </label>
        <ImageUpload
          onImageUploaded={handleImageUploaded}
          onImageRemoved={handleImageRemoved}
          currentImage={quizData.questionImage}
          className="mb-4"
          currentUser={currentUser}
        />
      </div>

      <div>
        <label htmlFor="quiz-question-mc" className="block text-sm font-medium text-purple-700 mb-1">
          問題文{quizData.hasImage ? '（画像がある場合は任意）' : ''}
        </label>
        <textarea
          id="quiz-question-mc"
          name="question"
          value={quizData.question}
          onChange={(e) => updateQuizData({ question: e.target.value })}
          placeholder={quizData.hasImage ? "画像の補足説明（任意）" : "例: 2 + 2 は何ですか？"}
          className="w-full p-2 border border-gray-300 rounded"
          rows={3}
        />
        {formErrors.question && <p className="text-red-600 text-sm">{formErrors.question}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-purple-700 mb-1">選択肢</label>
        {[0, 1, 2, 3].map((index) => (
          <input
            key={index}
            type="text"
            value={quizData.choices[index] || ''}
            onChange={(e) => {
              const newChoices = [...quizData.choices];
              newChoices[index] = e.target.value;
              updateQuizData({ choices: newChoices });
            }}
            placeholder={`選択肢 ${index + 1}`}
            className="w-full p-2 border rounded mb-2"
          />
        ))}
        {formErrors.choices && <p className="text-red-600 text-sm">{formErrors.choices}</p>}
      </div>

      <div>
        <label htmlFor="quiz-answer-mc" className="block text-sm font-medium text-purple-700 mb-1">
          正解
        </label>
        <input
          type="text"
          id="quiz-answer-mc"
          name="answer"
          value={quizData.answer}
          onChange={(e) => updateQuizData({ answer: e.target.value })}
          placeholder="正解を入力"
          className="w-full p-2 border border-gray-300 rounded"
        />
        {formErrors.answer && <p className="text-red-600 text-sm">{formErrors.answer}</p>}
      </div>
    </>
  );

  // 英文並び替え問題のフォーム
  const renderSentenceRearrangementForm = () => (
    <>
      <div>
        <label htmlFor="quiz-english-sentence" className="block text-sm font-medium text-purple-700 mb-1">
          英語の文
        </label>
        <textarea
          id="quiz-english-sentence"
          name="englishSentence"
          value={quizData.englishSentence}
          onChange={(e) => updateQuizData({ englishSentence: e.target.value })}
          placeholder="例: I am eating an apple."
          className="w-full p-2 border border-gray-300 rounded"
          rows={3}
        />
        {formErrors.englishSentence && (
          <p className="text-red-600 text-sm">{formErrors.englishSentence}</p>
        )}
      </div>

      <div>
        <label htmlFor="quiz-japanese-translation" className="block text-sm font-medium text-purple-700 mb-1">
          日本語訳
        </label>
        <textarea
          id="quiz-japanese-translation"
          name="japaneseTranslation"
          value={quizData.japaneseTranslation}
          onChange={(e) => updateQuizData({ japaneseTranslation: e.target.value })}
          placeholder="例: 私はリンゴを食べています。"
          className="w-full p-2 border border-gray-300 rounded"
          rows={3}
        />
        {formErrors.japaneseTranslation && (
          <p className="text-red-600 text-sm">{formErrors.japaneseTranslation}</p>
        )}
      </div>
    </>
  );

  // 正誤問題のフォーム
  const renderTrueFalseForm = () => (
    <>
      {/* 問題画像のアップロード */}
      <div>
        <label className="block text-sm font-medium text-purple-700 mb-2">
          問題画像（任意）
        </label>
        <ImageUpload
          onImageUploaded={handleImageUploaded}
          onImageRemoved={handleImageRemoved}
          currentImage={quizData.questionImage}
          className="mb-4"
          currentUser={currentUser}
        />
      </div>

      <div>
        <label htmlFor="quiz-question-tf" className="block text-sm font-medium text-purple-700 mb-1">
          問題文{quizData.hasImage ? '（画像がある場合は任意）' : ''}
        </label>
        <textarea
          id="quiz-question-tf"
          name="question"
          value={quizData.question}
          onChange={(e) => updateQuizData({ question: e.target.value })}
          placeholder={quizData.hasImage ? "画像の補足説明（任意）" : "問題文を入力してください"}
          className="w-full p-2 border border-gray-300 rounded"
          rows={3}
        />
        {formErrors.question && <p className="text-red-600 text-sm">{formErrors.question}</p>}
      </div>

      <div>
        <label htmlFor="quiz-answer-tf" className="block text-sm font-medium text-purple-700 mb-1">
          正解
        </label>
        <select
          id="quiz-answer-tf"
          value={quizData.answer}
          onChange={(e) => updateQuizData({ answer: e.target.value })}
          className="w-full p-2 border border-gray-300 rounded"
        >
          <option value="">正解を選択</option>
          <option value="true">正しい</option>
          <option value="false">正しくない</option>
        </select>
        {formErrors.answer && <p className="text-red-600 text-sm">{formErrors.answer}</p>}
      </div>
    </>
  );

  // 穴埋め問題のフォーム
  const renderFillInTheBlankForm = () => (
    <>
      {/* 問題画像のアップロード */}
      <div>
        <label className="block text-sm font-medium text-purple-700 mb-2">
          問題画像（任意）
        </label>
        <ImageUpload
          onImageUploaded={handleImageUploaded}
          onImageRemoved={handleImageRemoved}
          currentImage={quizData.questionImage}
          className="mb-4"
          currentUser={currentUser}
        />
      </div>

      <div>
        <label htmlFor="quiz-question-fill" className="block text-sm font-medium text-purple-700 mb-1">
          問題文{quizData.hasImage ? '（画像がある場合は任意）' : ''}
        </label>
        <textarea
          id="quiz-question-fill"
          name="question"
          value={quizData.question}
          onChange={(e) => updateQuizData({ question: e.target.value })}
          placeholder={quizData.hasImage ? "画像の補足説明（任意）" : "問題文を入力してください"}
          className="w-full p-2 border border-gray-300 rounded"
          rows={3}
        />
        {formErrors.question && <p className="text-red-600 text-sm">{formErrors.question}</p>}
      </div>

      <div>
        <label htmlFor="quiz-answer-fill" className="block text-sm font-medium text-purple-700 mb-1">
          正解
        </label>
        <input
          type="text"
          id="quiz-answer-fill"
          value={quizData.answer}
          onChange={(e) => updateQuizData({ answer: e.target.value })}
          placeholder="正解を入力"
          className="w-full p-2 border border-gray-300 rounded"
        />
        {formErrors.answer && <p className="text-red-600 text-sm">{formErrors.answer}</p>}
      </div>
    </>
  );

  const renderQuestionForm = () => {
    switch (quizData.type) {
      case 'multiple-choice':
        return renderMultipleChoiceForm();
      case 'sentence-rearrangement':
        return renderSentenceRearrangementForm();
      case 'true-false':
        return renderTrueFalseForm();
      case 'fill-in-the-blank':
        return renderFillInTheBlankForm();
      case 'matching':
        return <div className="p-4 bg-yellow-50 rounded">マッチング問題は後日実装予定です</div>;
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="absolute inset-0 bg-black opacity-50" />
      <div className="relative bg-white rounded-lg shadow-lg max-w-2xl w-full m-4 overflow-hidden">
        <div className="p-6">
          <div className="max-h-[90vh] overflow-y-auto">
            {/* ヘッダー */}
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-purple-700">
                {mode === 'edit' ? 'クイズを編集' : '新しいクイズを追加'}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              {/* クイズ形式選択 */}
              <div>
                <label htmlFor="quiz-type" className="block text-sm font-medium text-purple-700 mb-1">
                  クイズ形式
                </label>
                <select
                  id="quiz-type"
                  name="type"
                  value={quizData.type}
                  onChange={(e) => updateQuizData({ type: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded"
                  disabled={mode === 'edit'}
                >
                  <option value="multiple-choice">ボタンを利用した四択問題</option>
                  <option value="sentence-rearrangement">英文並び替え問題</option>
                  <option value="true-false">正誤問題</option>
                  <option value="fill-in-the-blank">穴埋め問題</option>
                  <option value="matching">マッチング問題</option>
                </select>
                {formErrors.type && <p className="text-red-600 text-sm">{formErrors.type}</p>}
              </div>

              {/* 教科選択 */}
              <div>
                <label htmlFor="quiz-subject" className="block text-sm font-medium text-purple-700 mb-1">
                  教科
                </label>
                <select
                  id="quiz-subject"
                  name="subject"
                  value={quizData.subject}
                  onChange={(e) => updateQuizData({ subject: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded"
                >
                  <option value="">教科を選択</option>
                  {SUBJECTS.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.name}
                    </option>
                  ))}
                </select>
                {formErrors.subject && <p className="text-red-600 text-sm">{formErrors.subject}</p>}
              </div>

              {/* 問題タイプ別フォーム */}
              {renderQuestionForm()}

              {/* 共通の解説フィールド */}
              <div>
                <label htmlFor="quiz-explanation" className="block text-sm font-medium text-purple-700 mb-1">
                  解説（任意）
                </label>
                <textarea
                  id="quiz-explanation"
                  name="explanation"
                  value={quizData.explanation}
                  onChange={(e) => updateQuizData({ explanation: e.target.value })}
                  placeholder="解説を入力してください（任意）"
                  className="w-full p-2 border border-gray-300 rounded"
                  rows={3}
                />
              </div>
            </div>

            {/* フッター */}
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
              >
                {mode === 'edit' ? '更新' : '追加'}
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                キャンセル
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

QuizEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['add', 'edit']).isRequired,
  quiz: PropTypes.object,
  contentSubject: PropTypes.string,
  handleSubmitQuiz: PropTypes.func.isRequired,
  formErrors: PropTypes.object,
  currentUser: PropTypes.object,
};

export default QuizEditModal; 